/**
 * Asiakkaan ja projektin lisäattribuutit.
 *
 * Attribuutti on **määritelty avain**, ei vapaa avain-arvopari. Ero on
 * koko mallin ydin, ja siihen on kolme syytä:
 *
 *   1. **Vienti on sarakkeita.** CSV:n otsikkorivi on laskutusajon
 *      sopimus. Vapaat avaimet tarkoittaisivat sarakejoukkoa, joka
 *      vaihtuu asiakkaan mukaan — tai yhtä möykkysaraketta, jota
 *      Excelissä ei voi käyttää.
 *   2. **Kirjoitusasu hajoaa.** Ilman määrittelyä samasta asiasta syntyy
 *      `projektikoodi`, `Projektikoodi` ja `proj.koodi`, ja raportilla ne
 *      ovat kolme eri riviä.
 *   3. **Muutosloki on kenttätasoinen.** Määritelty avain lokittuu omana
 *      kenttänään; JSON-möykky lokittuisi yhtenä läpinäkymättömänä erona.
 *
 * Avain on tekninen tunniste (`projektikoodi`) ja otsikko sen näytettävä
 * muoto (`Projektikoodi`). Avainta ei muuteta jälkikäteen: se on
 * CSV-sarakkeen nimi ja viennin valinnan tunniste, ja molemmat elävät
 * käyttäjien tallentamissa osoitteissa ja skripteissä.
 */
/**
 * Mihin attribuutti kiinnittyy.
 *
 * `BOTH` on periytyvä: arvo annetaan asiakkaalle ja projekti voi
 * ylikirjoittaa sen omallaan. Sama ketju kuin tuntihinnalla (5.2), ja
 * samasta syystä — asiakkaan kustannuspaikka on useimmiten myös sen
 * projektien kustannuspaikka, mutta ei aina.
 */
export const ATTRIBUTE_TARGETS = ['CUSTOMER', 'PROJECT', 'BOTH'];
/**
 * Avaimen muoto: pieniä kirjaimia, numeroita ja alaviivoja.
 *
 * Rajaus on tiukka tarkoituksella. Avain päätyy CSV:n otsikkoriville,
 * JSON-vientiin ja kyselyparametriin, eikä yksikään niistä kestä
 * välilyöntejä, ääkkösiä tai puolipisteitä ilman lainausta. Ihmiselle
 * tarkoitettu muoto on `label`, joka saa olla mitä tahansa.
 */
export const ATTRIBUTE_KEY_PATTERN = /^[a-z][a-z0-9_]*$/;
export const MAX_ATTRIBUTE_KEY_LENGTH = 40;
export const MAX_ATTRIBUTE_LABEL_LENGTH = 80;
export const MAX_ATTRIBUTE_VALUE_LENGTH = 200;
/** Kuinka monta attribuuttia järjestelmässä voi olla yhtä aikaa. */
export const MAX_ATTRIBUTE_DEFINITIONS = 50;
export function isAttributeTarget(value) {
    return typeof value === 'string' && ATTRIBUTE_TARGETS.includes(value);
}
/** Voiko tämän kohteen attribuutin antaa asiakkaalle? */
export function appliesToCustomer(target) {
    return target === 'CUSTOMER' || target === 'BOTH';
}
/** Voiko tämän kohteen attribuutin antaa projektille? */
export function appliesToProject(target) {
    return target === 'PROJECT' || target === 'BOTH';
}
/**
 * Projektin näkyvät attribuutit: omat arvot, ja periytyvien osalta
 * asiakkaan arvo silloin kun projektilla ei ole omaa.
 *
 * Tyhjä merkkijono on sama kuin puuttuva arvo. Kanta ei tallenna tyhjää
 * (sovellus poistaa rivin), mutta sääntö on tässäkin, koska periytymisen
 * pitää toimia samoin riippumatta siitä, kumpi kerros arvon antoi.
 */
export function resolveProjectAttributes(definitions, projectValues, customerValues) {
    const resolved = [];
    for (const definition of definitions) {
        if (!definition.active || !appliesToProject(definition.target))
            continue;
        const own = projectValues[definition.key];
        if (own) {
            resolved.push({ key: definition.key, label: definition.label, value: own, source: 'PROJECT' });
            continue;
        }
        const inherited = definition.target === 'BOTH' ? customerValues[definition.key] : undefined;
        if (inherited) {
            resolved.push({
                key: definition.key,
                label: definition.label,
                value: inherited,
                source: 'CUSTOMER',
            });
        }
    }
    return resolved;
}
/** Asiakkaan näkyvät attribuutit. Periytymistä ei ole — tämä on ketjun latva. */
export function resolveCustomerAttributes(definitions, customerValues) {
    const resolved = [];
    for (const definition of definitions) {
        if (!definition.active || !appliesToCustomer(definition.target))
            continue;
        const value = customerValues[definition.key];
        if (value) {
            resolved.push({
                key: definition.key,
                label: definition.label,
                value,
                source: 'CUSTOMER',
            });
        }
    }
    return resolved;
}
//# sourceMappingURL=attributes.js.map